// keyrd_console/src/components/NudgePlanBuilder.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { buildKeyrdHeaders } from "../api/headers";

function DiagnosisTagPill({ code, label }) {
  if (!code) return null;
  const title = label ? `${code} — ${label}` : code;
  return (
    <span
      title={title}
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "9999px",
        fontSize: "0.75rem",
        border: "1px solid #ddd",
        marginRight: "4px",
        marginBottom: "4px",
        backgroundColor: "#f7f7f7",
      }}
    >
      {code}
    </span>
  );
}

function FieldBadge({ label }) {
  if (!label) return null;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 6px",
        borderRadius: "4px",
        fontSize: "0.7rem",
        backgroundColor: "#eee",
        marginRight: "4px",
      }}
    >
      {label}
    </span>
  );
}

export default function NudgePlanBuilder(props) {
  // Supports either:
  //   <NudgePlanBuilder auth={auth} patientId={selectedPatientId} />
  //   <NudgePlanBuilder apiBaseUrl={...} rdnToken={...} selectedPatientId={...} />
  const {
    auth,
    patientId,
    apiBaseUrl,
    rdnToken,
    selectedPatientId: selectedPatientIdProp,
    // optional: { "NI-1.1": "Inadequate energy intake", ... }
    ncptLookup,
  } = props || {};

  const baseUrl = apiBaseUrl || auth?.baseUrl || "http://localhost:8000";
  const token = rdnToken || auth?.token || "";
  const selectedPatientId =
    patientId || selectedPatientIdProp || auth?.selectedPatientId || null;

  const [loading, setLoading] = useState(false);
  const [planRows, setPlanRows] = useState([]);
  const [error, setError] = useState(null);

  // diagnosis_families from backend (DiagnosisFamilyMap joined to Diagnosis)
  const [diagnosisFamilies, setDiagnosisFamilies] = useState([]);
  // family_templates from backend (NCPT family_key -> templates[])
  const [familyTemplates, setFamilyTemplates] = useState({});

  // add-row form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [formTemplateId, setFormTemplateId] = useState("1");
  const [formPriority, setFormPriority] = useState("1");
  const [formDiagTags, setFormDiagTags] = useState("");
  const [formActive, setFormActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // auto-seed state
  const [autoSeeding, setAutoSeeding] = useState(false);

  // currently selected NCPT problem (diagnosis code) in the sidebar
  const [selectedProblemCode, setSelectedProblemCode] = useState(null);

  // -------------------------------------------------------------------
  // NCPT diagnosis + family metadata
  // -------------------------------------------------------------------

  // Merge ncptLookup (if provided) with labels coming from diagnosis_families
  const diagnosisMeta = useMemo(() => {
    const base = ncptLookup ? { ...ncptLookup } : {};
    if (Array.isArray(diagnosisFamilies)) {
      for (const fam of diagnosisFamilies) {
        if (fam && fam.code && fam.label && !base[fam.code]) {
          base[fam.code] = fam.label;
        }
      }
    }
    return base;
  }, [ncptLookup, diagnosisFamilies]);

  // Group diagnosis families by diagnosis code, for high-level problem listing
  const groupedFamilies = useMemo(() => {
    if (!Array.isArray(diagnosisFamilies)) return [];
    const byCode = {};
    for (const fam of diagnosisFamilies) {
      if (!fam || !fam.code) continue;
      if (!byCode[fam.code]) {
        byCode[fam.code] = {
          code: fam.code,
          label: fam.label || null,
          families: [],
        };
      }
      if (
        fam.family_key &&
        !byCode[fam.code].families.includes(fam.family_key)
      ) {
        byCode[fam.code].families.push(fam.family_key);
      }
    }
    return Object.values(byCode);
  }, [diagnosisFamilies]);

  const hasDiagnosisFamilies =
    Array.isArray(groupedFamilies) && groupedFamilies.length > 0;

  // Map of diagnosis code -> [family_key, ...]
  const codeToFamilies = useMemo(() => {
    const map = {};
    if (!Array.isArray(diagnosisFamilies)) return map;
    for (const fam of diagnosisFamilies) {
      if (!fam || !fam.code || !fam.family_key) continue;
      if (!map[fam.code]) {
        map[fam.code] = [];
      }
      if (!map[fam.code].includes(fam.family_key)) {
        map[fam.code].push(fam.family_key);
      }
    }
    return map;
  }, [diagnosisFamilies]);

  // Patient-level NCPT family vector — union of all family keys present
  const patientFamilyKeys = useMemo(() => {
    const keys = new Set();
    Object.values(codeToFamilies).forEach((arr) => {
      (arr || []).forEach((k) => {
        if (k) keys.add(k);
      });
    });
    return Array.from(keys).sort();
  }, [codeToFamilies]);

  // For a given row, derive the NCPT family keys driving it from its codes
  const deriveRowFamilies = useCallback(
    (codes) => {
      if (!Array.isArray(codes) || codes.length === 0) return [];
      const keys = new Set();
      for (const c of codes) {
        const fams = codeToFamilies[c] || [];
        for (const f of fams) {
          if (f) keys.add(f);
        }
      }
      return Array.from(keys).sort();
    },
    [codeToFamilies]
  );

  // -------------------------------------------------------------------
  // Template-based family coverage + suggestions
  // -------------------------------------------------------------------

  const familyCoverage = useMemo(() => {
    const keys = patientFamilyKeys.slice().sort();
    const templatesByKey =
      familyTemplates && typeof familyTemplates === "object"
        ? familyTemplates
        : {};

    const details = keys.map((familyKey) => {
      const rawTemplates = Array.isArray(templatesByKey[familyKey])
        ? templatesByKey[familyKey]
        : [];

      const dedup = {};
      for (const t of rawTemplates) {
        if (!t || t.template_id == null) continue;
        const key = String(t.template_id);
        if (!dedup[key]) {
          dedup[key] = t;
        }
      }

      const templates = Object.values(dedup);
      return {
        familyKey,
        hasTemplates: templates.length > 0,
        templates,
      };
    });

    const totalFamilies = details.length;
    const coveredFamilies = details.filter((d) => d.hasTemplates).length;
    const uncoveredFamilies = totalFamilies - coveredFamilies;
    const missingFamilyKeys = details
      .filter((d) => !d.hasTemplates)
      .map((d) => d.familyKey);

    return {
      details,
      totalFamilies,
      coveredFamilies,
      uncoveredFamilies,
      missingFamilyKeys,
    };
  }, [patientFamilyKeys, familyTemplates]);

  const familySuggestions = useMemo(() => {
    if (!familyCoverage || !Array.isArray(familyCoverage.details)) return [];
    const suggestions = [];

    for (const item of familyCoverage.details) {
      if (!item || !item.familyKey) continue;
      if (!item.hasTemplates) continue;

      const rawTemplates = Array.isArray(item.templates)
        ? item.templates
        : [];

      const dedup = {};
      for (const t of rawTemplates) {
        if (!t || t.template_id == null) continue;
        const key = String(t.template_id);
        if (!dedup[key]) {
          dedup[key] = t;
        }
      }

      const dedupList = Object.values(dedup);
      if (dedupList.length === 0) continue;

      const top = dedupList.slice(0, 3);
      suggestions.push({
        familyKey: item.familyKey,
        templates: top,
      });
    }

    suggestions.sort((a, b) => a.familyKey.localeCompare(b.familyKey));
    return suggestions;
  }, [familyCoverage]);

  // -------------------------------------------------------------------
  // Plan-level coverage: which families are already represented in rows
  // (used to skip families when auto-seeding)
  // -------------------------------------------------------------------

  const planCoveredFamilyKeys = useMemo(() => {
    const covered = new Set();
    if (!Array.isArray(planRows) || planRows.length === 0) {
      return [];
    }

    for (const row of planRows) {
      if (!row) continue;
      const tmpl = row.template || {};
      const rowDiagCodes = Array.isArray(row.diagnosis_tags)
        ? row.diagnosis_tags
        : [];
      const tmplDiagCodes = Array.isArray(tmpl.diagnosis_tags)
        ? tmpl.diagnosis_tags
        : [];
      const diagnosisCodes =
        rowDiagCodes.length > 0 ? rowDiagCodes : tmplDiagCodes;

      const fams = deriveRowFamilies(diagnosisCodes);
      for (const f of fams) {
        if (f) covered.add(f);
      }
    }

    return Array.from(covered).sort();
  }, [planRows, deriveRowFamilies]);

  // Map familyKey -> suggestion object for quick lookup
  const suggestionMap = useMemo(() => {
    const map = {};
    for (const s of familySuggestions) {
      if (!s || !s.familyKey) continue;
      map[s.familyKey] = s;
    }
    return map;
  }, [familySuggestions]);

  // Families that:
  //   - exist for this patient (patientFamilyKeys)
  //   - have recommended templates (familySuggestions)
  //   - are NOT already covered in the current plan
  const autoSeedTargets = useMemo(() => {
    if (!Array.isArray(patientFamilyKeys) || patientFamilyKeys.length === 0) {
      return [];
    }
    const covered = new Set(planCoveredFamilyKeys);
    const targets = [];
    for (const fKey of patientFamilyKeys) {
      if (!suggestionMap[fKey]) continue; // no templates to seed
      if (covered.has(fKey)) continue; // already represented in plan
      targets.push(fKey);
    }
    return targets;
  }, [patientFamilyKeys, planCoveredFamilyKeys, suggestionMap]);

  // -------------------------------------------------------------------
  // Per-problem coverage (for sidebar badges) + snapshot
  // -------------------------------------------------------------------

  const problemCoverage = useMemo(() => {
    const map = {};
    if (!Array.isArray(planRows) || planRows.length === 0) {
      return map;
    }

    const codes = Object.keys(codeToFamilies);
    if (codes.length === 0) return map;

    for (const code of codes) {
      const families = codeToFamilies[code] || [];
      let activeCount = 0;
      let totalCount = 0;

      for (const row of planRows) {
        if (!row) continue;
        const tmpl = row.template || {};
        const rowDiagCodes = Array.isArray(row.diagnosis_tags)
          ? row.diagnosis_tags
          : [];
        const tmplDiagCodes = Array.isArray(tmpl.diagnosis_tags)
          ? tmpl.diagnosis_tags
          : [];
        const diagnosisCodes =
          rowDiagCodes.length > 0 ? rowDiagCodes : tmplDiagCodes;

        const rowFamilies = deriveRowFamilies(diagnosisCodes);
        const intersects = rowFamilies.some((f) => families.includes(f));
        if (!intersects) continue;

        totalCount += 1;
        if (row.active) {
          activeCount += 1;
        }
      }

      map[code] = { activeCount, totalCount };
    }

    return map;
  }, [planRows, codeToFamilies, deriveRowFamilies]);

  const problemsList = useMemo(() => {
    if (!Array.isArray(groupedFamilies)) return [];
    return groupedFamilies.map((group) => {
      const cov = problemCoverage[group.code] || {
        activeCount: 0,
        totalCount: 0,
      };
      return {
        ...group,
        activeCount: cov.activeCount,
        totalCount: cov.totalCount,
      };
    });
  }, [groupedFamilies, problemCoverage]);

  const planSnapshot = useMemo(() => {
    const totalRows = Array.isArray(planRows) ? planRows.length : 0;
    const activeRows = Array.isArray(planRows)
      ? planRows.filter((r) => r && r.active).length
      : 0;

    const totalProblems = Array.isArray(problemsList) ? problemsList.length : 0;
    const problemsWithAnyNudges = Array.isArray(problemsList)
      ? problemsList.filter((p) => p.totalCount > 0).length
      : 0;

    return {
      totalRows,
      activeRows,
      totalProblems,
      problemsWithAnyNudges,
    };
  }, [planRows, problemsList]);

  // -------------------------------------------------------------------
  // Filtering by selected problem
  // -------------------------------------------------------------------

  const selectedFamilies = useMemo(() => {
    if (!selectedProblemCode) return null;
    return codeToFamilies[selectedProblemCode] || [];
  }, [selectedProblemCode, codeToFamilies]);

  const visibleFamilySuggestions = useMemo(() => {
    if (!selectedFamilies) return familySuggestions;
    return familySuggestions.filter((item) =>
      selectedFamilies.includes(item.familyKey)
    );
  }, [familySuggestions, selectedFamilies]);

  const filteredPlanRows = useMemo(() => {
    if (!Array.isArray(planRows)) return [];
    if (!selectedFamilies || selectedFamilies.length === 0) return planRows;

    return planRows.filter((row) => {
      if (!row) return false;
      const tmpl = row.template || {};
      const rowDiagCodes = Array.isArray(row.diagnosis_tags)
        ? row.diagnosis_tags
        : [];
      const tmplDiagCodes = Array.isArray(tmpl.diagnosis_tags)
        ? tmpl.diagnosis_tags
        : [];
      const diagnosisCodes =
        rowDiagCodes.length > 0 ? rowDiagCodes : tmplDiagCodes;

      const rowFamilies = deriveRowFamilies(diagnosisCodes);
      return rowFamilies.some((f) => selectedFamilies.includes(f));
    });
  }, [planRows, selectedFamilies, deriveRowFamilies]);

  const hasAnyPlanRows =
    Array.isArray(planRows) && planRows.length > 0;
  const hasVisiblePlanRows =
    Array.isArray(filteredPlanRows) && filteredPlanRows.length > 0;

  // ----------------------------------
  // Load current plan
  // ----------------------------------
  const loadPlan = useCallback(async () => {
    if (!selectedPatientId || !token) {
      setPlanRows([]);
      setDiagnosisFamilies([]);
      setFamilyTemplates({});
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `${baseUrl}/console/patients/${selectedPatientId}/plan`,
        {
          method: "GET",
          headers: buildKeyrdHeaders({ token }),
        }
      );

      const data = await res.json();

      if (!res.ok || data.ok === false) {
        setError(
          (data && data.error) ||
            `HTTP ${res.status} while loading nudge plan`
        );
        setPlanRows([]);
        setDiagnosisFamilies([]);
        setFamilyTemplates({});
      } else {
        setPlanRows(Array.isArray(data.plan) ? data.plan : []);
        setDiagnosisFamilies(
          Array.isArray(data.diagnosis_families)
            ? data.diagnosis_families
            : []
        );
        setFamilyTemplates(
          data.family_templates && typeof data.family_templates === "object"
            ? data.family_templates
            : {}
        );
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error loading plan:", err);
      setError("network_error");
      setPlanRows([]);
      setDiagnosisFamilies([]);
      setFamilyTemplates({});
    } finally {
      setLoading(false);
    }
  }, [baseUrl, token, selectedPatientId]);

  useEffect(() => {
    // Reset local state when switching patients
    setPlanRows([]);
    setDiagnosisFamilies([]);
    setFamilyTemplates({});
    setError(null);
    setShowAddForm(false);
    setSubmitError(null);
    setAutoSeeding(false);
    setSelectedProblemCode(null);
    if (selectedPatientId && token) {
      void loadPlan();
    }
  }, [selectedPatientId, token, loadPlan]);

  // ----------------------------------
  // PATCH / DELETE helpers
  // ----------------------------------
  const patchPlanRow = useCallback(
    async (planId, payload) => {
      if (!selectedPatientId || !token) return;

      try {
        const res = await fetch(
          `${baseUrl}/console/patients/${selectedPatientId}/plan/${planId}`,
          {
            method: "PATCH",
            headers: buildKeyrdHeaders({ token, contentType: "application/json" }),
            body: JSON.stringify(payload),
          }
        );

        let data = {};
        try {
          data = await res.json();
        } catch {
          // tolerate empty body
        }

        if (!res.ok || data.ok === false) {
          // eslint-disable-next-line no-console
          console.error("PATCH plan row failed:", data || res.status);
          window.alert(
            (data && data.error) ||
              `Failed to update plan row (HTTP ${res.status}).`
          );
          return;
        }

        await loadPlan();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error patching plan row:", err);
        window.alert("Network error while updating plan row.");
      }
    },
    [baseUrl, token, selectedPatientId, loadPlan]
  );

  const deletePlanRow = useCallback(
    async (planId) => {
      if (!selectedPatientId || !token) return;

      try {
        const res = await fetch(
          `${baseUrl}/console/patients/${selectedPatientId}/plan/${planId}`,
          {
            method: "DELETE",
            headers: buildKeyrdHeaders({ token }),
          }
        );

        let data = {};
        try {
          data = await res.json();
        } catch {
          // tolerate empty body
        }

        if (!res.ok || data.ok === false) {
          // eslint-disable-next-line no-console
          console.error("DELETE plan row failed:", data || res.status);
          window.alert(
            (data && data.error) ||
              `Failed to delete plan row (HTTP ${res.status}).`
          );
          return;
        }

        await loadPlan();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error deleting plan row:", err);
        window.alert("Network error while deleting plan row.");
      }
    },
    [baseUrl, token, selectedPatientId, loadPlan]
  );

  // ----------------------------------
  // Use a suggested family to prefill the add-row form (by diagnosis code)
  // ----------------------------------
  const handleApplyFamilySuggestion = useCallback((code, familyKey) => {
    setShowAddForm(true);

    setFormDiagTags((prev) => {
      const current = (prev || "").trim();
      if (!current) return code;

      const tokens = current
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      if (tokens.includes(code)) {
        return tokens.join(", ");
      }
      return [...tokens, code].join(", ");
    });

    // eslint-disable-next-line no-console
    console.debug("Applied family suggestion:", code, familyKey);
  }, []);

  // ----------------------------------
  // Click a suggested template → auto-populate the form
  // ----------------------------------
  function handleChooseSuggestedTemplate(familyKey, template) {
    if (!template) return;

    const codesForFamily = Array.isArray(diagnosisFamilies)
      ? diagnosisFamilies
          .filter((df) => df.family_key === familyKey)
          .map((df) => df.code)
          .join(", ")
      : "";

    const priorities = Array.isArray(planRows)
      ? planRows
          .map((row) => row.priority)
          .filter((p) => typeof p === "number" && !Number.isNaN(p))
      : [];

    const nextPriority =
      priorities.length > 0 ? Math.max(...priorities) + 1 : 1;

    setFormTemplateId(String(template.template_id));
    setFormPriority(String(nextPriority));
    setFormDiagTags(codesForFamily);
    setFormActive(true);
    setShowAddForm(true);
  }

  // ----------------------------------
  // NCPT-aware auto-seed: Add all recommended nudges
  // ----------------------------------
  const handleAutoSeedRecommended = useCallback(async () => {
    if (!selectedPatientId || !token) return;
    if (!Array.isArray(autoSeedTargets) || autoSeedTargets.length === 0) {
      window.alert(
        "No uncovered NCPT families with recommended templates to auto-seed."
      );
      return;
    }

    // Compute base priority from existing plan
    const existingPriorities = Array.isArray(planRows)
      ? planRows
          .map((row) => row.priority)
          .filter((p) => typeof p === "number" && !Number.isNaN(p))
      : [];
    let nextPriority =
      existingPriorities.length > 0 ? Math.max(...existingPriorities) + 1 : 1;

    setAutoSeeding(true);

    try {
      let createdCount = 0;
      let failedCount = 0;

      for (const familyKey of autoSeedTargets) {
        const suggestion = suggestionMap[familyKey];
        if (!suggestion || !Array.isArray(suggestion.templates)) continue;
        if (suggestion.templates.length === 0) continue;

        const topTemplate = suggestion.templates[0];
        if (!topTemplate || topTemplate.template_id == null) continue;

        // Collect NCPT diagnosis codes for this family
        const codesForFamily = Array.isArray(diagnosisFamilies)
          ? diagnosisFamilies
              .filter((df) => df.family_key === familyKey && df.code)
              .map((df) => df.code)
          : [];

        const body = {
          template_id: parseInt(topTemplate.template_id, 10),
          priority: nextPriority,
          diagnosis_tags: Array.from(new Set(codesForFamily)),
          active: true,
        };

        try {
          const res = await fetch(
            `${baseUrl}/console/patients/${selectedPatientId}/plan`,
            {
              method: "POST",
              headers: buildKeyrdHeaders({ token, contentType: "application/json" }),
              body: JSON.stringify(body),
            }
          );

          let data = {};
          try {
            data = await res.json();
          } catch {
            // tolerate empty body
          }

          if (!res.ok || data.ok === false) {
            // eslint-disable-next-line no-console
            console.error(
              "Auto-seed create plan row failed:",
              familyKey,
              data || res.status
            );
            failedCount += 1;
          } else {
            createdCount += 1;
            nextPriority += 1;
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Error auto-seeding plan row:", familyKey, err);
          failedCount += 1;
        }
      }

      if (createdCount === 0 && failedCount === 0) {
        window.alert(
          "No eligible recommended templates were found to auto-seed."
        );
      } else if (createdCount > 0 && failedCount === 0) {
        window.alert(
          `Added ${createdCount} recommended nudge${
            createdCount === 1 ? "" : "s"
          } to the plan.`
        );
      } else {
        window.alert(
          `Attempted to add ${createdCount + failedCount} recommended nudges.\n` +
            `${createdCount} succeeded, ${failedCount} failed. Check console logs for details.`
        );
      }

      await loadPlan();
    } finally {
      setAutoSeeding(false);
    }
  }, [
    autoSeedTargets,
    baseUrl,
    diagnosisFamilies,
    loadPlan,
    planRows,
    selectedPatientId,
    suggestionMap,
    token,
  ]);

  // ----------------------------------
  // Create plan row (POST) via manual form
  // ----------------------------------
  const handleCreatePlanRow = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!selectedPatientId || !token) {
      setSubmitError("missing_auth_or_patient");
      return;
    }

    const templateIdInt = parseInt(formTemplateId, 10);
    const priorityInt =
      formPriority && !Number.isNaN(parseInt(formPriority, 10))
        ? parseInt(formPriority, 10)
        : null;

    if (!templateIdInt || Number.isNaN(templateIdInt)) {
      setSubmitError("template_id_invalid");
      return;
    }

    const tags =
      formDiagTags && formDiagTags.trim().length > 0
        ? formDiagTags
            .split(",")
            .map((t) => t.trim())
            .filter((t) => t.length > 0)
        : [];

    const body = {
      template_id: templateIdInt,
      priority: priorityInt,
      diagnosis_tags: tags,
      active: !!formActive,
    };

    setSubmitting(true);
    try {
      const res = await fetch(
        `${baseUrl}/console/patients/${selectedPatientId}/plan`,
        {
          method: "POST",
          headers: buildKeyrdHeaders({ token, contentType: "application/json" }),
          body: JSON.stringify(body),
        }
      );

      const data = await res.json();

      if (!res.ok || data.ok === false) {
        // eslint-disable-next-line no-console
        console.error("Create plan row failed:", data);
        setSubmitError(
          (data && data.error) ||
            `HTTP ${res.status} while creating plan row`
        );
        return;
      }

      setShowAddForm(false);
      setFormTemplateId(String(templateIdInt));
      setFormPriority("1");
      setFormDiagTags(tags.join(", "));
      setFormActive(true);

      await loadPlan();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error creating plan row:", err);
      setSubmitError("network_error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (row) => {
    if (!row || !row.id) return;
    await patchPlanRow(row.id, { active: !row.active });
  };

  const handleChangePriority = async (row) => {
    if (!row || !row.id) return;

    const current =
      typeof row.priority === "number" && Number.isFinite(row.priority)
        ? row.priority
        : "";

    const input = window.prompt(
      "Set priority (integer, 1 = highest).",
      current === "" ? "" : String(current)
    );

    if (input === null) return;

    const parsed = parseInt(input, 10);
    if (Number.isNaN(parsed) || parsed < 1) {
      window.alert("Priority must be a positive integer.");
      return;
    }

    await patchPlanRow(row.id, { priority: parsed });
  };

  const handleDeleteRow = async (row) => {
    if (!row || !row.id) return;

    const confirmed = window.confirm(
      "Remove this patient nudge plan row?\n\nThis will not delete the underlying template."
    );
    if (!confirmed) return;

    await deletePlanRow(row.id);
  };

  // ----------------------------------
  // Render
  // ----------------------------------
  if (!selectedPatientId) {
    return (
      <div style={{ padding: "16px" }}>
        <h3 style={{ marginBottom: "8px" }}>Nudge plan</h3>
        <p style={{ color: "#666", fontSize: "0.9rem" }}>
          No patient selected — choose one from the Patient List.
        </p>
      </div>
    );
  }

  const hasFamilyCoverageDetails =
    familyCoverage &&
    Array.isArray(familyCoverage.details) &&
    familyCoverage.details.length > 0;

  const uncoveredFamilies = hasFamilyCoverageDetails
    ? familyCoverage.details.filter((d) => !d.hasTemplates)
    : [];

  return (
    <div style={{ padding: "16px" }}>
      {/* Header row with Add button */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "8px",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <h3 style={{ margin: 0 }}>Nudge plan for this patient</h3>
            <span
              style={{
                marginLeft: "8px",
                fontSize: "0.8rem",
                color: "#888",
              }}
            >
              Patient: {selectedPatientId}
            </span>
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "#555",
              marginTop: "2px",
            }}
          >
            Check that each coded NCPT problem has at least one appropriate
            nudge, then fine-tune priorities.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowAddForm((prev) => !prev)}
          style={{
            padding: "6px 12px",
            borderRadius: "4px",
            border: "1px solid #0b3d2e",
            backgroundColor: "#0b3d2e",
            color: "#fff",
            fontSize: "0.8rem",
            cursor: "pointer",
          }}
        >
          {showAddForm ? "Cancel" : "Add nudge"}
        </button>
      </div>

      {/* Plan snapshot bar */}
      <div
        style={{
          borderRadius: "6px",
          border: "1px solid #e0e0e0",
          padding: "6px 10px",
          marginBottom: "10px",
          backgroundColor: "#f9fafb",
          fontSize: "0.8rem",
        }}
      >
        <strong>Plan snapshot:</strong>{" "}
        {planSnapshot.activeRows} active of {planSnapshot.totalRows} nudges ·{" "}
        NCPT coverage: {planSnapshot.problemsWithAnyNudges}/
        {planSnapshot.totalProblems} problems have at least one mapped nudge.
      </div>

      {/* Two-column layout: left = problems, right = suggestions + plan */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "12px",
        }}
      >
        {/* LEFT COLUMN: NCPT problem list */}
        <div
          style={{
            width: "260px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
              padding: "8px 10px",
              backgroundColor: "#fafafa",
            }}
          >
            <div
              style={{
                fontSize: "0.85rem",
                fontWeight: 600,
                marginBottom: "4px",
              }}
            >
              NCPT nutrition problems
            </div>
            {problemsList.length === 0 ? (
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#666",
                }}
              >
                No NCPT problems coded yet for this patient.
              </div>
            ) : (
              <>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#555",
                    marginBottom: "6px",
                  }}
                >
                  Click a problem to filter recommendations and plan rows.
                </div>
                {selectedProblemCode && (
                  <div
                    style={{
                      fontSize: "0.7rem",
                      color: "#2563eb",
                      marginBottom: "4px",
                      cursor: "pointer",
                    }}
                    onClick={() => setSelectedProblemCode(null)}
                  >
                    Clear filter
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    maxHeight: "420px",
                    overflowY: "auto",
                  }}
                >
                  {problemsList.map((p) => {
                    const isSelected = p.code === selectedProblemCode;

                    let badgeLabel = "No nudges";
                    let badgeBg = "#fef2f2";
                    let badgeBorder = "1px solid #fecaca";
                    let badgeColor = "#b91c1c";

                    if (p.totalCount > 0 && p.activeCount === 0) {
                      badgeLabel = "Inactive only";
                      badgeBg = "#fffbeb";
                      badgeBorder = "1px solid #facc15";
                      badgeColor = "#92400e";
                    } else if (p.activeCount > 0) {
                      badgeLabel = `${p.activeCount} active`;
                      badgeBg = "#e6f4ea";
                      badgeBorder = "1px solid #6abf69";
                      badgeColor = "#166534";
                    }

                    return (
                      <button
                        key={p.code}
                        type="button"
                        onClick={() =>
                          setSelectedProblemCode(
                            isSelected ? null : p.code
                          )
                        }
                        style={{
                          textAlign: "left",
                          width: "100%",
                          borderRadius: "6px",
                          border: isSelected
                            ? "1px solid #0b3d2e"
                            : "1px solid #e5e5e5",
                          backgroundColor: isSelected
                            ? "#e6f4ea"
                            : "#ffffff",
                          padding: "6px 8px",
                          cursor: "pointer",
                          fontSize: "0.8rem",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            gap: "6px",
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              {p.code}
                            </div>
                            {p.label && (
                              <div
                                style={{
                                  fontSize: "0.75rem",
                                  color: "#555",
                                }}
                              >
                                {p.label}
                              </div>
                            )}
                          </div>
                          <span
                            style={{
                              padding: "2px 6px",
                              borderRadius: "9999px",
                              backgroundColor: badgeBg,
                              border: badgeBorder,
                              color: badgeColor,
                              fontSize: "0.7rem",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {badgeLabel}
                          </span>
                        </div>
                        {Array.isArray(p.families) &&
                          p.families.length > 0 && (
                            <div
                              style={{
                                marginTop: "4px",
                                fontSize: "0.7rem",
                                color: "#666",
                              }}
                            >
                              Families: {p.families.join(", ")}
                            </div>
                          )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: suggestions, form, plan rows */}
        <div style={{ flex: 1 }}>
          {/* Suggested NCPT nudge families (code -> family_key chips) */}
          {hasDiagnosisFamilies && (
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                padding: "8px 10px",
                marginBottom: "8px",
                backgroundColor: "#fdfdfd",
              }}
            >
              <div
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  marginBottom: "4px",
                }}
              >
                What this patient needs (NCPT nutrition problems)
              </div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#555",
                  marginBottom: "6px",
                }}
              >
                Each coded problem should have at least one matching nudge in
                your plan.
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                {groupedFamilies.map((group) => (
                  <div key={group.code} style={{ fontSize: "0.8rem" }}>
                    <div style={{ marginBottom: "2px" }}>
                      <strong>{group.code}</strong>
                      {group.label ? ` — ${group.label}` : null}
                    </div>
                    {group.families && group.families.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                        }}
                      >
                        {group.families.map((fKey) => (
                          <button
                            key={fKey}
                            type="button"
                            onClick={() =>
                              handleApplyFamilySuggestion(
                                group.code,
                                fKey
                              )
                            }
                            style={{
                              padding: "2px 8px",
                              borderRadius: "9999px",
                              border: "1px solid #ccc",
                              backgroundColor: "#f5f5f5",
                              fontSize: "0.7rem",
                              cursor: "pointer",
                            }}
                            title={`Use ${group.code} for family "${fKey}"`}
                          >
                            {fKey}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top-level NCPT family coverage bar (template mapping) */}
          {hasFamilyCoverageDetails && (
            <div
              style={{
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
                padding: "8px 10px",
                marginBottom: "8px",
                backgroundColor: "#fefefe",
              }}
            >
              <div
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  marginBottom: "4px",
                }}
              >
                NCPT family coverage for this patient
              </div>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#444",
                  marginBottom: "4px",
                }}
              >
                {familyCoverage.coveredFamilies} of{" "}
                {familyCoverage.totalFamilies} NCPT families have mapped
                nudge templates.
              </div>
              {familyCoverage.uncoveredFamilies > 0 && (
                <>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#555",
                      marginBottom: "4px",
                    }}
                  >
                    Families without mapped templates (first four):
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "4px",
                    }}
                  >
                    {familyCoverage.missingFamilyKeys
                      .slice(0, 4)
                      .map((fKey) => (
                        <span
                          key={fKey}
                          style={{
                            padding: "2px 8px",
                            borderRadius: "9999px",
                            border: "1px dashed #f97373",
                            backgroundColor: "#fff7f7",
                            fontSize: "0.7rem",
                            color: "#b91c1c",
                          }}
                        >
                          {fKey}
                        </span>
                      ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Per-family NCPT template suggestions with click-to-seed + auto-seed button */}
          {visibleFamilySuggestions.length > 0 && (
            <div
              style={{
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
                padding: "8px 10px",
                marginBottom: "12px",
                backgroundColor: "#fdfdfd",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "4px",
                }}
              >
                <div
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: 600,
                  }}
                >
                  Recommended nudges for these problems
                </div>
                <button
                  type="button"
                  onClick={handleAutoSeedRecommended}
                  disabled={
                    autoSeeding ||
                    !Array.isArray(autoSeedTargets) ||
                    autoSeedTargets.length === 0
                  }
                  style={{
                    padding: "4px 10px",
                    borderRadius: "9999px",
                    border: "1px solid #0b3d2e",
                    backgroundColor:
                      autoSeeding ||
                      !Array.isArray(autoSeedTargets) ||
                      autoSeedTargets.length === 0
                        ? "#ccc"
                        : "#0b3d2e",
                    color: "#fff",
                    fontSize: "0.75rem",
                    cursor:
                      autoSeeding ||
                      !Array.isArray(autoSeedTargets) ||
                      autoSeedTargets.length === 0
                        ? "default"
                        : "pointer",
                    whiteSpace: "nowrap",
                  }}
                  title={
                    autoSeeding
                      ? "Auto-seeding in progress…"
                      : "Add one recommended nudge per uncovered NCPT family."
                  }
                >
                  {autoSeeding
                    ? "Adding recommended nudges…"
                    : "Add all recommended"}
                </button>
              </div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#555",
                  marginBottom: "6px",
                }}
              >
                Click a recommended nudge to pre-fill the form with template
                ID, next priority, and NCPT codes for that problem family.
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                {visibleFamilySuggestions.map((item) => (
                  <div
                    key={item.familyKey}
                    style={{
                      borderRadius: "6px",
                      border: "1px solid #eee",
                      padding: "6px 8px",
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        marginBottom: "4px",
                      }}
                    >
                      {item.familyKey}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      {item.templates.map((t) => (
                        <div
                          key={`${item.familyKey}-${t.template_id}`}
                          onClick={() =>
                            handleChooseSuggestedTemplate(
                              item.familyKey,
                              t
                            )
                          }
                          style={{
                            padding: "8px 12px",
                            borderRadius: 4,
                            border: "1px solid #ddd",
                            marginBottom: 6,
                            cursor: "pointer",
                            background: "#fafafa",
                          }}
                        >
                          <div style={{ fontWeight: 500 }}>
                            {t.title || `Template #${t.template_id}`}
                          </div>
                          <div
                            style={{
                              fontSize: "0.8rem",
                              color: "#555",
                            }}
                          >
                            ID: {t.template_id} · Cooldown:{" "}
                            {t.cooldown_hours != null
                              ? `${t.cooldown_hours}h`
                              : "—"}
                          </div>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: t.safe_for_disordered_eating_risk
                                ? "#1a7f37"
                                : "#b91c1c",
                              marginTop: 2,
                            }}
                          >
                            {t.safe_for_disordered_eating_risk
                              ? "Flagged safe for ED risk."
                              : "Not flagged safe for ED risk."}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Create-row form */}
          {showAddForm && (
            <form
              onSubmit={handleCreatePlanRow}
              style={{
                border: "1px solid #ddd",
                borderRadius: "8px",
                padding: "10px 12px",
                marginBottom: "12px",
                backgroundColor: "#fafafa",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px 16px",
                  marginBottom: "8px",
                }}
              >
                <div style={{ minWidth: "120px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.75rem",
                      color: "#555",
                      marginBottom: "2px",
                    }}
                  >
                    Template ID
                  </label>
                  <input
                    type="number"
                    value={formTemplateId}
                    onChange={(e) => setFormTemplateId(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "4px 6px",
                      fontSize: "0.8rem",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                    }}
                    min={1}
                    required
                  />
                </div>

                <div style={{ minWidth: "100px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.75rem",
                      color: "#555",
                      marginBottom: "2px",
                    }}
                  >
                    Priority
                  </label>
                  <input
                    type="number"
                    value={formPriority}
                    onChange={(e) => setFormPriority(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "4px 6px",
                      fontSize: "0.8rem",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                    }}
                    min={1}
                  />
                </div>

                <div style={{ flex: 1, minWidth: "220px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.75rem",
                      color: "#555",
                      marginBottom: "2px",
                    }}
                  >
                    NCPT diagnosis codes (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={formDiagTags}
                    onChange={(e) => setFormDiagTags(e.target.value)}
                    placeholder="e.g., NI-1.1, NI-2.2"
                    style={{
                      width: "100%",
                      padding: "4px 6px",
                      fontSize: "0.8rem",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                    }}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: "100px",
                    marginTop: "18px",
                  }}
                >
                  <input
                    id="plan-active-checkbox"
                    type="checkbox"
                    checked={formActive}
                    onChange={(e) => setFormActive(e.target.checked)}
                    style={{ marginRight: "6px" }}
                  />
                  <label
                    htmlFor="plan-active-checkbox"
                    style={{ fontSize: "0.8rem", color: "#333" }}
                  >
                    Active
                  </label>
                </div>
              </div>

              {submitError && (
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#b00020",
                    marginBottom: "6px",
                  }}
                >
                  Create failed: {String(submitError)}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                style={{
                  padding: "6px 12px",
                  borderRadius: "4px",
                  border: "1px solid #0b3d2e",
                  backgroundColor: submitting ? "#999" : "#0b3d2e",
                  color: "#fff",
                  fontSize: "0.8rem",
                  cursor: submitting ? "default" : "pointer",
                }}
              >
                {submitting ? "Saving…" : "Save nudge"}
              </button>
            </form>
          )}

          {loading && (
            <p style={{ fontSize: "0.9rem", color: "#666" }}>
              Loading plan…
            </p>
          )}

          {error && !loading && (
            <p style={{ fontSize: "0.9rem", color: "#b00020" }}>
              Error loading plan: {String(error)}
            </p>
          )}

          {!loading && !error && !hasAnyPlanRows && (
            <p style={{ fontSize: "0.9rem", color: "#666" }}>
              No plan rows configured yet for this patient.
            </p>
          )}

          {!loading &&
            !error &&
            hasAnyPlanRows &&
            !hasVisiblePlanRows &&
            selectedProblemCode && (
              <p style={{ fontSize: "0.85rem", color: "#92400e" }}>
                No nudges yet for this problem. Use the recommendations
                above to add your first nudge.
              </p>
            )}

          {!loading && !error && hasVisiblePlanRows && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                marginTop: "4px",
              }}
            >
              {/* High-level summary of patient NCPT families (for context) */}
              {patientFamilyKeys.length > 0 && (
                <div
                  style={{
                    borderRadius: "8px",
                    border: "1px dashed #e0e0e0",
                    padding: "6px 8px",
                    marginBottom: "4px",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#555",
                      marginBottom: "2px",
                    }}
                  >
                    NCPT nutrition problem families in play:
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "4px",
                    }}
                  >
                    {patientFamilyKeys.map((fKey) => (
                      <span
                        key={fKey}
                        style={{
                          padding: "2px 8px",
                          borderRadius: "9999px",
                          border: "1px solid #ddd",
                          backgroundColor: "#f9f9f9",
                          fontSize: "0.7rem",
                        }}
                      >
                        {fKey}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* NCPT families lacking mapped templates (registry gaps) */}
              {hasFamilyCoverageDetails && (
                <div
                  style={{
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                    padding: "8px 10px",
                    marginBottom: "4px",
                    backgroundColor: "#fefefe",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      marginBottom: "4px",
                    }}
                  >
                    Nutrition problems without mapped nudges (gaps)
                  </div>
                  {uncoveredFamilies.length === 0 ? (
                    <div
                      style={{
                        fontSize: "0.8rem",
                        color: "#166534",
                      }}
                    >
                      All NCPT nutrition problem families for this patient
                      currently have at least one mapped nudge template.
                    </div>
                  ) : (
                    <>
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#555",
                          marginBottom: "6px",
                        }}
                      >
                        The following NCPT families are present for this
                        patient but have no mapped nudge templates yet.
                        Consider creating new templates or expanding
                        coverage.
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                        }}
                      >
                        {uncoveredFamilies.map((fam) => (
                          <span
                            key={fam.familyKey}
                            style={{
                              padding: "2px 8px",
                              borderRadius: "9999px",
                              border: "1px dashed #f97373",
                              backgroundColor: "#fff7f7",
                              fontSize: "0.7rem",
                              color: "#b91c1c",
                            }}
                          >
                            {fam.familyKey}
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Individual plan rows (filtered if a problem is selected) */}
              {filteredPlanRows.map((row) => {
                const tmpl = row.template || {};
                const rowDiagCodes = row.diagnosis_tags || [];
                const tmplDiagCodes = tmpl.diagnosis_tags || [];

                const diagnosisCodes =
                  rowDiagCodes.length > 0 ? rowDiagCodes : tmplDiagCodes;

                const rowFamilies = deriveRowFamilies(diagnosisCodes);

                const isActive = !!row.active;
                const priority =
                  typeof row.priority === "number" ? row.priority : null;

                const title =
                  tmpl.name ||
                  tmpl.label ||
                  tmpl.title ||
                  `Template #${tmpl.id ?? "?"}`;
                const body = tmpl.body || "";
                const category = tmpl.category || null;
                const riskProfile =
                  row.risk_profile || tmpl.risk_profile || null;
                const intensity = row.intensity || tmpl.intensity || null;
                const behaviorTags =
                  row.behavior_tags ||
                  tmpl.behavior_tags ||
                  tmpl.tags ||
                  null;

                const createdAt = row.created_at || null;
                const updatedAt = row.updated_at || null;

                return (
                  <div
                    key={row.id || `${tmpl.id}-${priority ?? "na"}`}
                    style={{
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      padding: "10px 12px",
                      backgroundColor: isActive ? "#ffffff" : "#fafafa",
                      opacity: isActive ? 1 : 0.7,
                    }}
                  >
                    {/* Header line with controls */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "6px",
                        gap: "8px",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            marginBottom: "2px",
                          }}
                        >
                          {title}
                        </div>
                        <div
                          style={{ fontSize: "0.8rem", color: "#777" }}
                        >
                          {category
                            ? `Category: ${category}`
                            : "Uncategorized"}
                        </div>
                      </div>

                      <div
                        style={{
                          textAlign: "right",
                          fontSize: "0.8rem",
                          minWidth: "140px",
                        }}
                      >
                        <div
                          style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: "9999px",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            backgroundColor: isActive
                              ? "#e6f4ea"
                              : "#f1f1f1",
                            border: isActive
                              ? "1px solid #6abf69"
                              : "1px solid #ccc",
                            color: isActive ? "#256029" : "#555",
                            marginBottom: "4px",
                          }}
                        >
                          {isActive ? "ACTIVE" : "INACTIVE"}
                        </div>
                        {priority !== null && (
                          <div style={{ color: "#666" }}>
                            Priority {priority}
                          </div>
                        )}

                        <div
                          style={{
                            marginTop: "4px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "2px",
                            alignItems: "flex-end",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => handleToggleActive(row)}
                            style={{
                              border: "none",
                              background: "none",
                              color: "#2563eb",
                              fontSize: "0.75rem",
                              cursor: "pointer",
                              padding: 0,
                            }}
                          >
                            {isActive ? "Mark inactive" : "Mark active"}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleChangePriority(row)
                            }
                            style={{
                              border: "none",
                              background: "none",
                              color: "#2563eb",
                              fontSize: "0.75rem",
                              cursor: "pointer",
                              padding: 0,
                            }}
                          >
                            Edit priority…
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteRow(row)}
                            style={{
                              border: "none",
                              background: "none",
                              color: "#b91c1c",
                              fontSize: "0.75rem",
                              cursor: "pointer",
                              padding: 0,
                            }}
                          >
                            Delete row
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Body snippet */}
                    {body && (
                      <div
                        style={{
                          fontSize: "0.85rem",
                          marginBottom: "6px",
                          color: "#333",
                        }}
                      >
                        {body.length > 140
                          ? body.slice(0, 140) + "…"
                          : body}
                      </div>
                    )}

                    {/* NCPT Diagnosis tags */}
                    {diagnosisCodes && diagnosisCodes.length > 0 && (
                      <div style={{ marginBottom: "6px" }}>
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "#555",
                            marginBottom: "2px",
                          }}
                        >
                          NCPT diagnoses:
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                          }}
                        >
                          {diagnosisCodes.map((code) => (
                            <DiagnosisTagPill
                              key={code}
                              code={code}
                              label={diagnosisMeta[code]}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* NCPT families driving this row */}
                    {rowFamilies.length > 0 && (
                      <div style={{ marginBottom: "6px" }}>
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "#555",
                            marginBottom: "2px",
                          }}
                        >
                          NCPT families driving this row:
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                          }}
                        >
                          {rowFamilies.map((fKey) => (
                            <span
                              key={fKey}
                              style={{
                                padding: "2px 8px",
                                borderRadius: "9999px",
                                border: "1px solid #ddd",
                                backgroundColor: "#f4f4ff",
                                fontSize: "0.7rem",
                              }}
                            >
                              {fKey}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Risk / intensity / behavior tags */}
                    <div style={{ marginBottom: "4px" }}>
                      {riskProfile && (
                        <FieldBadge label={`Risk: ${riskProfile}`} />
                      )}
                      {intensity && (
                        <FieldBadge label={`Intensity: ${intensity}`} />
                      )}
                      {behaviorTags &&
                        Array.isArray(behaviorTags) &&
                        behaviorTags.map((b) => (
                          <FieldBadge
                            key={b}
                            label={`Behavior: ${b}`}
                          />
                        ))}
                    </div>

                    {/* Timestamps */}
                    {(createdAt || updatedAt) && (
                      <div
                        style={{
                          fontSize: "0.7rem",
                          color: "#888",
                          marginTop: "4px",
                        }}
                      >
                        {createdAt && (
                          <span>
                            Created:{" "}
                            {String(createdAt).replace("Z", "")}
                          </span>
                        )}
                        {createdAt && updatedAt && <span> · </span>}
                        {updatedAt && (
                          <span>
                            Updated:{" "}
                            {String(updatedAt).replace("Z", "")}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
